import { flagSet } from '@coreui/icons';
import { removeAuthLocalStorage } from '../auths/Auth';

export interface ServerEnvironmentType {
  serverPath: string;
  name: string;
  displayName: string;
  countryFlagIcon?: (typeof flagSet)[keyof typeof flagSet]; // Optional, type-safe reference to flagSet icons
  countryFlagIconName?: string; // Optional
  color: string;
  appPath: string;
  partnerAppPath: string;
  auth0Domain: string;
  auth0Audience: string;
  auth0Namespace: string;
  auth0ClientId: string;
  v2: {
    serverPath: string;
  };
  tenantHubServerLocation: string;
}

export const ServerEnvironment: Record<string, ServerEnvironmentType> =
  Object.freeze({
    Development: {
      serverPath: 'https://localhost:7058',
      name: 'Development',
      displayName: 'Development',
      color: 'secondary',
      partnerAppPath: 'https://partner-uat.sleekflow.io',
      appPath: 'https://dev.sleekflow.io',
      auth0Domain: 'sso-dev.sf.chat',
      auth0Audience: 'https://api-dev.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'IRQqRRw0m9mPHhpfm3Gg68xv9lLmaSBQ',
      v2: {
        serverPath: 'http://localhost:8080/v1'
      },
      tenantHubServerLocation: 'eastasia'
    },
    Dev: {
      serverPath: 'https://sleekflow-powerflow-app-eas-dev.azurewebsites.net',
      name: 'Dev',
      displayName: 'Dev',
      countryFlagIcon: flagSet.cifHk,
      countryFlagIconName: 'cif-hk',
      color: 'info',
      partnerAppPath: 'https://partner-uat.sleekflow.io',
      appPath: 'https://dev.sleekflow.io',
      auth0Domain: 'sso-dev.sf.chat',
      auth0Audience: 'https://api-dev.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'IRQqRRw0m9mPHhpfm3Gg68xv9lLmaSBQ',
      v2: {
        serverPath: 'https://sleekflow-dev-gug7frbbe9grfvhh.z01.azurefd.net/v1'
      },
      tenantHubServerLocation: 'eastasia'
    },
    Staging: {
      serverPath:
        'https://sleekflow-powerflow-app-eas-staging.azurewebsites.net',
      name: 'Staging',
      displayName: 'Staging',
      countryFlagIcon: flagSet.cifHk,
      countryFlagIconName: 'cif-hk',
      color: 'warning',
      appPath: 'https://staging.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso-staging.sleekflow.io',
      auth0Audience: 'https://api-staging.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'StYdG2sHFsVeLjq9tNcuM6kREh1SEfFj',
      v2: {
        serverPath:
          'https://sleekflow-staging-arcycrczafgrescq.z01.azurefd.net/v1'
      },
      tenantHubServerLocation: 'eastasia'
    },
    Staging_East_Us: {
      serverPath:
        'https://sleekflow-powerflow-app-eus-staging.azurewebsites.net',
      name: 'Staging_East_Us',
      displayName: 'Staging (East US)',
      countryFlagIcon: flagSet.cifUs,
      countryFlagIconName: 'cif-us',
      color: 'warning',
      appPath: 'https://staging.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso-staging.sleekflow.io',
      auth0Audience: 'https://api-staging.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'StYdG2sHFsVeLjq9tNcuM6kREh1SEfFj',
      v2: {
        serverPath:
          'https://sleekflow-staging-arcycrczafgrescq.z01.azurefd.net/v1'
      },
      tenantHubServerLocation: 'eastus'
    },

    // We don't have a dedicated performance environment for powerflow yet
    // Please use the production one for now
    // Performance:{
    //   serverPath: 'https://powerflow-api.azurewebsites.net',
    //   name: 'Performance',
    //   displayName: 'Performance',
    //   countryFlagIcon: flagSet.cifHk,
    //   countryFlagIconName: 'cif-hk',
    //   color: 'primary',
    //   appPath: 'https://perf.sleekflow.io',
    //   partnerAppPath: 'https://partner.sleekflow.io',
    //   auth0Domain: 'sso.sleekflow.io',
    //   auth0Audience: 'https://api.sleekflow.io',
    //   auth0Namespace: 'https://app.sleekflow.io/',
    //   auth0ClientId: 'T7GgXHOSi6oSVKiZSgYkoxilJcIp9obC',
    //   v2: {
    //     serverPath: 'https://gw.sleekflow.io/v1'
    //   }
    // },

    Production_East_Asia: {
      serverPath:
        'https://sleekflow-powerflow-app-eas-production.azurewebsites.net',
      name: 'Production_East_Asia',
      displayName: 'Production (East Asia)',
      countryFlagIcon: flagSet.cifHk,
      countryFlagIconName: 'cif-hk',
      color: 'primary',
      appPath: 'https://app.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso.sleekflow.io',
      auth0Audience: 'https://api.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'T7GgXHOSi6oSVKiZSgYkoxilJcIp9obC',
      v2: {
        serverPath: 'https://gw.sleekflow.io/v1'
      },
      tenantHubServerLocation: 'eastasia'
    },
    Production_Stand_By: {
      serverPath:
        'https://sleekflow-powerflow-app-eas-production.azurewebsites.net',
      name: 'Production_Stand_By',
      displayName: 'Production (StandBy)',
      countryFlagIcon: flagSet.cifHk,
      countryFlagIconName: 'cif-hk',
      color: 'warning',
      appPath: 'https://perf.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso.sleekflow.io',
      auth0Audience: 'https://api.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'T7GgXHOSi6oSVKiZSgYkoxilJcIp9obC',
      v2: {
        serverPath: 'https://gw.sleekflow.io/v1'
      },
      tenantHubServerLocation: 'eastasia'
    },
    Production_East_Us: {
      serverPath:
        'https://sleekflow-powerflow-app-eus-production.azurewebsites.net',
      name: 'Production_East_Us',
      displayName: 'Production (East US)',
      countryFlagIcon: flagSet.cifUs,
      countryFlagIconName: 'cif-us',
      color: 'primary',
      appPath: 'https://app.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso.sleekflow.io',
      auth0Audience: 'https://api.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'T7GgXHOSi6oSVKiZSgYkoxilJcIp9obC',
      v2: {
        serverPath: 'https://gw.sleekflow.io/v1'
      },
      tenantHubServerLocation: 'eastus'
    },
    Production_South_East_Asia: {
      serverPath:
        'https://sleekflow-powerflow-app-seas-production.azurewebsites.net',
      name: 'Production_South_East_Asia',
      displayName: 'Production (South East Asia)',
      countryFlagIcon: flagSet.cifSg,
      countryFlagIconName: 'cif-sg',
      color: 'primary',
      appPath: 'https://app.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso.sleekflow.io',
      auth0Audience: 'https://api.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'T7GgXHOSi6oSVKiZSgYkoxilJcIp9obC',
      v2: {
        serverPath: 'https://gw.sleekflow.io/v1'
      },
      tenantHubServerLocation: 'southeastasia'
    },
    Production_United_Arab_Emirates_North: {
      serverPath:
        'https://sleekflow-powerflow-app-uaen-production.azurewebsites.net',
      name: 'Production_United_Arab_Emirates_North',
      displayName: 'Production (United Arab Emirates North)',
      countryFlagIcon: flagSet.cifAe,
      countryFlagIconName: 'cif-ae',
      color: 'primary',
      appPath: 'https://app.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso.sleekflow.io',
      auth0Audience: 'https://api.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'T7GgXHOSi6oSVKiZSgYkoxilJcIp9obC',
      v2: {
        serverPath: 'https://gw.sleekflow.io/v1'
      },
      tenantHubServerLocation: 'uaenorth'
    },
    Production_West_Europe: {
      serverPath:
        'https://sleekflow-powerflow-app-weu-production.azurewebsites.net',
      name: 'Production_West_Europe',
      displayName: 'Production (West Europe)',
      countryFlagIcon: flagSet.cifNl,
      countryFlagIconName: 'cif-nl',
      color: 'primary',
      appPath: 'https://app.sleekflow.io',
      partnerAppPath: 'https://partner.sleekflow.io',
      auth0Domain: 'sso.sleekflow.io',
      auth0Audience: 'https://api.sleekflow.io',
      auth0Namespace: 'https://app.sleekflow.io/',
      auth0ClientId: 'T7GgXHOSi6oSVKiZSgYkoxilJcIp9obC',
      v2: {
        serverPath: 'https://gw.sleekflow.io/v1'
      },
      tenantHubServerLocation: 'westeurope'
    }
  });

export const ServerEnvironmentListForLogin = [
  ServerEnvironment.Dev,
  ServerEnvironment.Staging,
  ServerEnvironment.Staging_East_Us,
  ServerEnvironment.Production_Stand_By,
  ServerEnvironment.Production_East_Asia,
  ServerEnvironment.Production_East_Us,
  ServerEnvironment.Production_South_East_Asia,
  ServerEnvironment.Production_United_Arab_Emirates_North,
  ServerEnvironment.Production_West_Europe
];

export function setCmsServerEnvironment(env: ServerEnvironmentType) {
  removeAuthLocalStorage();

  localStorage.setItem('_env', env.name);
}

export function setCmsServerEnvironmentWithoutRemoveAuthLocalStorage(
  env: ServerEnvironmentType
) {
  localStorage.setItem('_env', env.name);
}

export function getCmsServerEnvironment() {
  const envName = localStorage.getItem('_env');

  return findCmsServerEnvironmentByName(envName);
}

export function findCmsServerEnvironmentByName(envName: string | null) {
  if (
    process.env.NODE_ENV === 'development' &&
    envName === ServerEnvironment.Development.name
  )
    return ServerEnvironment.Development;

  if (envName == null || envName === '')
    return process.env.NODE_ENV === 'development'
      ? ServerEnvironment.Development
      : ServerEnvironment.Production_East_Asia;

  return ServerEnvironmentListForLogin.find((item) => item.name === envName);
}

export function getCentralizedServerEnvironments() {
  const currentEnvironment = getCmsServerEnvironment();

  if (currentEnvironment) {
    if (currentEnvironment.name === 'Development') {
      return [currentEnvironment];
    }

    return ServerEnvironmentListForLogin.filter(
      (x) =>
        x.auth0ClientId === currentEnvironment.auth0ClientId &&
        x.name != 'Production_Stand_By'
    );
  }

  return [];
}

